import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Typography from '@mui/material/Typography'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import ResetFilterButton from '../../component/ResetFilterButton'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { LIST_FILTER_DIVIDER_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'
import {
    APP_TAB_PROMOTION_DEVICE_TYPE_OPTIONS,
    APP_TAB_PROMOTION_NAV_TAB_OPTIONS,
    APP_TAB_PROMOTION_STATUS_OPTIONS,
    APP_TAB_PROMOTION_TARGET_MEMBERSHIP_OPTIONS,
    getAppTabPromotionOptionLabel,
} from './appTabPromotionConstants'

const DEFAULT_SEARCH_SELECT = {
    navTab: '',
    targetMembership: '',
    deviceType: '',
    status: '',
}

const SEARCH_SELECT_PARAMS = {
    navTab: { param: 'tab', defaultValue: DEFAULT_SEARCH_SELECT.navTab },
    targetMembership: { param: 'target', defaultValue: DEFAULT_SEARCH_SELECT.targetMembership },
    deviceType: { param: 'device', defaultValue: DEFAULT_SEARCH_SELECT.deviceType },
    status: { param: 'status', defaultValue: DEFAULT_SEARCH_SELECT.status },
}

const TABLE_COLUMNS = [
    { width: 70 },
    { width: 110 },
    { width: 120 },
    { width: 140 },
    { width: 120 },
    { width: 95 },
    { width: 90 },
    { width: 240 },
    { width: 210 },
    { width: 120 },
]

const TABLE_MIN_WIDTH = TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const IMAGE_SX = {
    width: 84,
    height: 64,
    objectFit: 'contain',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: '4px',
    display: 'block',
}

const TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const NOWRAP_CELL_SX = { whiteSpace: 'nowrap' }

const getInitialListState = (location) => createInitialListState({
    location,
    defaultSearchSelect: DEFAULT_SEARCH_SELECT,
    selectParams: SEARCH_SELECT_PARAMS,
})

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => buildListSearchQuery({
    curPage,
    searchQuery,
    searchSelect,
    selectParams: SEARCH_SELECT_PARAMS,
})

const formatDateTime = (value) => {
    if (!value) return '-'
    return String(value).replace('T', ' ').slice(0, 16)
}

const getImageDimensionText = (row) => {
    const imageWidth = Number(row.imageWidth)
    const imageHeight = Number(row.imageHeight)

    if (!Number.isFinite(imageWidth) || imageWidth <= 0 || !Number.isFinite(imageHeight) || imageHeight <= 0) return '-'
    return `${imageWidth}x${imageHeight}`
}

export default function AppTabPromotions({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const silentListRefreshRef = useRef(false)
    const deleteInFlightRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) setLoading(true)
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appTabPromotion/getList', {
                page: curPage,
                offset,
                adminId,
                role,
                searchText: searchQuery,
                searchSelect,
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                return
            }

            if (data.code === 403) {
                history.push('/login')
                return
            }

            const errorMessage = data.message || 'App 탭 프로모션 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } catch (_error) {
            const errorMessage = 'App 탭 프로모션 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, history, role, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({ pathname: location.pathname, search: nextSearch })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => setOpenSnackbar(false)

    const updateSearchSelect = (e) => {
        if (actionLoading) return

        const { name, value } = e.target
        if (curPage === 1) silentListRefreshRef.current = true
        setSearchSelect(prev => ({ ...prev, [name]: value }))
        setCurPage(1)
    }

    const resetFilters = () => {
        if (actionLoading) return
        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({ ...DEFAULT_SEARCH_SELECT })
    }

    const searchButtonClick = () => {
        if (actionLoading) return
        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)
        if (curPage !== 1) setCurPage(1)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const handleDelete = (id) => {
        if (deleteInFlightRef.current) return

        deleteInFlightRef.current = true
        setActionLoading(true)

        const deleteData = async () => {
            try {
                const { data } = await API.post('/api/v1/admin/appTabPromotion/delete', { promotionId: id })
                if (data.code === 200) {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('success')
                    setMessageSnackbar(data.message)
                    if (curPage > 1 && params.length === 1) setCurPage(curPage - 1)
                    else await getData(false)
                } else if (data.code === 403) {
                    history.push('/login')
                } else {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(data.message)
                }
            } catch (_error) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('App 탭 프로모션을 삭제하지 못했습니다.')
            } finally {
                deleteInFlightRef.current = false
                setActionLoading(false)
            }
        }

        deleteData()
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || Object.entries(searchSelect).some(([key, value]) => String(value) !== String(DEFAULT_SEARCH_SELECT[key]))

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="App 탭 프로모션 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={<Button type="button" size="small" variant="outlined" onClick={() => getData(false)} disabled={actionLoading}>다시 조회</Button>}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 App 탭 프로모션이 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={actionLoading} />}
                />
            )
        }

        return <ListEmptyState title="등록된 App 탭 프로모션이 없습니다." description="프로모션 이미지를 등록하면 이 목록에 표시됩니다." />
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Title>App 탭 프로모션 리스트</Title>
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={actionLoading} />}>
                            <InputBase sx={LIST_FILTER_SEARCH_FIELD_SX} placeholder="제목, 이동대상, 템플릿" type="search" name="searchText" value={searchText} onChange={(e) => setSearchText(e.target.value)} disabled={actionLoading} />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={actionLoading}><SearchIcon /></IconButton>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            <TextField label="탭" select name="navTab" value={searchSelect.navTab} onChange={updateSearchSelect} disabled={actionLoading} size="small" sx={LIST_FILTER_SELECT_FIELD_SX}>
                                <MenuItem value="">전체</MenuItem>
                                {APP_TAB_PROMOTION_NAV_TAB_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                            </TextField>
                            <TextField label="대상" select name="targetMembership" value={searchSelect.targetMembership} onChange={updateSearchSelect} disabled={actionLoading} size="small" sx={LIST_FILTER_SELECT_FIELD_SX}>
                                <MenuItem value="">전체</MenuItem>
                                {APP_TAB_PROMOTION_TARGET_MEMBERSHIP_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                            </TextField>
                            <TextField label="기기" select name="deviceType" value={searchSelect.deviceType} onChange={updateSearchSelect} disabled={actionLoading} size="small" sx={LIST_FILTER_SELECT_FIELD_SX}>
                                <MenuItem value="">전체</MenuItem>
                                {APP_TAB_PROMOTION_DEVICE_TYPE_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                            </TextField>
                            <TextField label="상태" select name="status" value={searchSelect.status} onChange={updateSearchSelect} disabled={actionLoading} size="small" sx={LIST_FILTER_SELECT_FIELD_SX}>
                                <MenuItem value="">전체</MenuItem>
                                {APP_TAB_PROMOTION_STATUS_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>

                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-appTabPromotion',
                                    search: location.search,
                                    curPage,
                                    searchText: searchQuery,
                                    searchSelect,
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                            <colgroup>
                                {TABLE_COLUMNS.map((column, index) => <col key={`app-tab-promotion-column-${index}`} style={{ width: column.width }} />)}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>노출상태</TableCell>
                                    <TableCell>탭</TableCell>
                                    <TableCell>대상</TableCell>
                                    <TableCell>기기</TableCell>
                                    <TableCell>국가</TableCell>
                                    <TableCell>우선순위</TableCell>
                                    <TableCell>이미지</TableCell>
                                    <TableCell>제목</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow key={row.promotionId}>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell sx={NOWRAP_CELL_SX}>{Number(row.status) === 1 ? '노출' : '미노출'}</TableCell>
                                            <TableCell sx={NOWRAP_CELL_SX}>{getAppTabPromotionOptionLabel(APP_TAB_PROMOTION_NAV_TAB_OPTIONS, row.navTab)}</TableCell>
                                            <TableCell sx={NOWRAP_CELL_SX}>{getAppTabPromotionOptionLabel(APP_TAB_PROMOTION_TARGET_MEMBERSHIP_OPTIONS, row.targetMembership)}</TableCell>
                                            <TableCell sx={NOWRAP_CELL_SX}>{getAppTabPromotionOptionLabel(APP_TAB_PROMOTION_DEVICE_TYPE_OPTIONS, row.deviceType)}</TableCell>
                                            <TableCell sx={NOWRAP_CELL_SX}>{row.cCode || '-'}</TableCell>
                                            <TableCell sx={NOWRAP_CELL_SX}>{row.priority}</TableCell>
                                            <TableCell>
                                                {row.image ? (
                                                    <>
                                                        <img src={`${process.env.REACT_APP_AWS_S3_URL}/${row.image}`} alt="" style={IMAGE_SX} />
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{getImageDimensionText(row)}</Typography>
                                                    </>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell sx={TEXT_CELL_SX} title={`${row.title || ''} ${formatDateTime(row.startTime)}~${formatDateTime(row.endTime)}`}>{row.title || '-'}</TableCell>
                                            <TableCell align="right" sx={NOWRAP_CELL_SX}>
                                                <ListActionGroup disabled={actionLoading}>
                                                    <EditIcon path="appTabPromotion" id={row.promotionId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect}/>
                                                    <DeleteAlertDialog handleDelete={handleDelete} id={row.promotionId}/>
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10}>{renderEmptyState()}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>} 
                </Paper>
            )}
        </Grid>
    )
}
